import { WorkExperience as WorkExperienceRecord } from '../../../../../../store/workspace/types';
import DateRangeFields from '../../../components/DateRangeFields';
import { useProfileUpdate } from '../../../hooks/useProfileUpdate';
import TextArea from '../../../../../../components/TextArea';
import Modal from '../../../../../../components/Modal';
import Input from '../../../../../../components/Input';
import styles from './style.module.scss';
import { v4 as uuid } from 'uuid';
import { useState } from 'react';

interface Props {
    /** The job being edited, or `null` when a new one is added. */
    experience: WorkExperienceRecord | null;
    onClose: () => void;
};

/** Adds a job to the profile or edits one that is already on it. */
function WorkExperienceForm({ experience, onClose }: Props) {
    const { profile, isSaving, saveProfile } = useProfileUpdate();

    const [to, setTo] = useState(experience?.to || '');
    const [from, setFrom] = useState(experience?.from || '');
    const [company, setCompany] = useState(experience?.company || '');
    const [jobTitle, setJobTitle] = useState(experience?.jobTitle || '');
    const [location, setLocation] = useState(experience?.location || '');
    const [roleDescription, setRoleDescription] = useState(experience?.roleDescription || '');
    const [currentlyWorkHere, setCurrentlyWorkHere] = useState(Boolean(experience && !experience.to));

    const submit = async () => {
        if (!profile) return;

        const record: WorkExperienceRecord = {
            from,
            company,
            jobTitle,
            location,
            roleDescription,
            id: experience?.id || uuid(),
            to: currentlyWorkHere ? '' : to,
        };

        const others = profile.workExperience.filter(i => i.id !== record.id);
        const saved = await saveProfile({ workExperience: [...others, record] });

        if (saved) onClose();
    };

    const isIncomplete =
        !from ||
        !company ||
        !location ||
        !jobTitle ||
        !roleDescription ||
        (!currentlyWorkHere && !to);

    return (
        <Modal
            size='md'
            actions='confirm'
            closeFunc={onClose}
            childClassName={styles.wrapper}
            title={experience ? 'Edit work experience' : 'Add work experience'}
            confirmButton={{
                text: 'Save',
                type: 'Primary',
                onClick: submit,
                loading: isSaving,
                disabled: isIncomplete,
            }}
        >
            <Input
                type='text'
                label='Job title'
                value={jobTitle}
                onChange={setJobTitle}
            />
            <Input
                type='text'
                label='Company'
                value={company}
                onChange={setCompany}
            />
            <Input
                type='text'
                label='Location'
                value={location}
                onChange={setLocation}
            />
            <TextArea
                label='Role description'
                value={roleDescription}
                onChange={setRoleDescription}
            />
            <DateRangeFields
                to={to}
                from={from}
                setTo={setTo}
                setFrom={setFrom}
                isOngoing={currentlyWorkHere}
                setIsOngoing={setCurrentlyWorkHere}
                ongoingLabel='I currently work here'
            />
        </Modal>
    );
};

export default WorkExperienceForm;
